import { DocumentDuplicateIcon, EyeIcon, EyeSlashIcon, PencilSquareIcon } from '@heroicons/react/24/outline'
import { GlobalConfigType, setGlobalConfig } from 'actions'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { accountTypes } from 'config/user.type.constants'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { getAdminConfig, setAdminConfig } from 'services'
import { getPermissionDetails, updatePermissions } from 'services/apis/admin'
import { svgLoading } from 'stories/assets'
import { Button, Checkbox, Select2, Tooltip } from 'stories/components'
import { confirm, confirmOptions } from 'utils'

interface IProjectPermissionTemplate {
  key: string
  title: string
  data?: IProjectPermissionTemplate[] | Record<string, string>
}

export function PermissionsByRole() {
  const dispatch = useDispatch()
  const pagePermissionsGlobal = useSelector(
    (state: any) => state.globalConfigReducer[GlobalConfigType.PagePermissions] || {},
  )
  const [isLoading, setIsLoading] = useState(false)
  const [permissions, setPermissions] = useState<Record<number, string>>({})
  const [roles, setRoles] = useState<Record<number, string>>({})
  const [permissionRoles, setPermissionRoles] = useState<Record<number, number[]>>({})
  const [permissionNames, setPermissionNames] = useState<Record<string, string>>({})
  const [orders, setOrders] = useState<number[]>([])

  const [editRoleID, setEditRoleID] = useState(-1)
  const [editPermissions, setEditPermissions] = useState<number[]>([])
  const [action, setAction] = useState('')

  // Page permissions (global + per account type)
  const [pageTemplates, setPageTemplates] = useState<IProjectPermissionTemplate[]>([])
  const [pageData, setPageData] = useState<Record<string, boolean>>({})
  const [pageCustom, setPageCustom] = useState<Record<string, boolean>>({})
  const [byAccountType, setByAccountType] = useState<Record<string, Record<string, boolean>>>({})
  const [editPageOverrides, setEditPageOverrides] = useState<Record<string, boolean>>({})

  const initData = async () => {
    setIsLoading(true)
    const [res, pagePerms] = await Promise.all([getPermissionDetails(), getAdminConfig('pagePermissions')])
    setIsLoading(false)
    if (res.success) {
      setPermissions(res.data.permissions)
      setRoles(res.data.roles)
      setPermissionRoles(res.data.permissionRoles)
      setPermissionNames(res.data.permissionNames)
      setOrders(res.data.orders)
    }
    setPageTemplates(pagePerms?.template || [])
    setPageData(pagePerms?.data || {})
    setPageCustom(pagePerms?.custom || {})
    setByAccountType(pagePerms?.byAccountType || {})
  }

  useEffect(() => {
    initData()
  }, [])

  const rolePermissions = useMemo(() => {
    const map: Record<number, number[]> = {}
    Object.keys(permissionRoles).forEach((id) => {
      const permissionId = Number(id)
      permissionRoles[permissionId].forEach((roleID) => {
        if (!map[roleID]) map[roleID] = []
        map[roleID].push(permissionId)
      })
    })
    return map
  }, [permissionRoles])

  const orderedPermissionIDs = useMemo(() => {
    const data = Object.keys(permissions).map((id, index) => ({
      id: Number(id),
      order: orders[index] ?? index,
    }))
    return data.sort((a, b) => a.order - b.order).map((d) => d.id)
  }, [permissions, orders])

  const groupedPermissions = useMemo(() => {
    const groups: { category: string; items: { id: number; displayName: string; shortName: string }[] }[] = []
    let currentCategory = ''
    orderedPermissionIDs.forEach((id) => {
      const name = permissions[id]
      if (!name) return
      const displayName = permissionNames[name] || name
      const dotIndex = name.indexOf('.')
      const category = dotIndex > -1 ? name.substring(0, dotIndex) : name
      if (category !== currentCategory) {
        groups.push({ category, items: [] })
        currentCategory = category
      }
      const shortName = displayName.startsWith(category + '.')
        ? displayName.substring(category.length + 1)
        : displayName
      groups[groups.length - 1].items.push({ id, displayName, shortName })
    })
    return groups
  }, [orderedPermissionIDs, permissions, permissionNames])

  const orderedRoles = useMemo(() => {
    return Object.keys(accountTypes)
      .map((key) => {
        const roleId = Object.keys(roles).find((id) => roles[Number(id)] === key)
        return roleId ? { key, label: accountTypes[key], roleID: Number(roleId) } : null
      })
      .filter(Boolean) as { key: string; label: string; roleID: number }[]
  }, [roles])

  const editingAccountType = editRoleID === -1 ? '' : roles[editRoleID] || ''

  const isGloballyEnabled = (pageKey: string) => pageData[pageKey] !== false && pageCustom[pageKey] !== false

  const onEditRole = (roleID: number) => {
    if (editRoleID !== -1) return
    setEditRoleID(roleID)
    setEditPermissions(cloneDeep(rolePermissions[roleID] || []))
    const accountType = roles[roleID]
    setEditPageOverrides(cloneDeep(byAccountType[accountType] || {}))
  }

  const copyPermissionsFrom = (sourceRoleID: number) => {
    setEditPermissions(cloneDeep(rolePermissions[sourceRoleID] || []))
    const sourceAccountType = roles[sourceRoleID]
    setEditPageOverrides(cloneDeep(byAccountType[sourceAccountType] || {}))
  }

  const onCopyFrom = async (sourceRoleKey: string) => {
    if (!sourceRoleKey) return
    const source = orderedRoles.find((r) => r.key === sourceRoleKey)
    if (!source || source.roleID === editRoleID) return
    const sourceLabel = source.label
    const destLabel = accountTypes[editingAccountType] || editingAccountType
    const ok = await confirm(
      `Replace ${destLabel}'s current draft with ${sourceLabel}'s capability permissions and page visibility? You can still fine-tune before saving.`,
    )
    if (!ok) return
    copyPermissionsFrom(source.roleID)
  }

  const onCopyRoleTo = async (sourceRoleID: number) => {
    if (editRoleID !== -1) {
      toast('Finish or cancel the account type you are editing before copying permissions.', { type: 'warning' })
      return
    }
    const source = orderedRoles.find((r) => r.roleID === sourceRoleID)
    const destinations = orderedRoles.filter((r) => r.roleID !== sourceRoleID)
    if (!source || !destinations.length) return

    const destRoleID = await confirmOptions(
      `Copy ${source.label} permissions to which account type?`,
      destinations.map((r) => ({ name: r.label, value: r.roleID })),
    )
    if (!destRoleID) return

    setEditRoleID(Number(destRoleID))
    copyPermissionsFrom(sourceRoleID)
  }

  const onTogglePermission = (permissionId: number, value: boolean) => {
    setEditPermissions((prev) => (value ? [...prev, permissionId] : prev.filter((id) => id !== permissionId)))
  }

  const onTogglePage = (pageKey: string) => {
    if (!isGloballyEnabled(pageKey)) return
    setEditPageOverrides((prev) => {
      const next = cloneDeep(prev)
      if (next[pageKey] === false) delete next[pageKey]
      else next[pageKey] = false
      return next
    })
  }

  const onSelectAll = () => setEditPermissions(cloneDeep(orderedPermissionIDs))

  const onEnableAllPages = () => {
    // Clear overrides → inherit all globally-enabled pages
    setEditPageOverrides({})
  }

  const onSaveChanges = async () => {
    setIsLoading(true)
    setAction('save')

    const original = new Set(rolePermissions[editRoleID] || [])
    const updated = new Set(editPermissions)
    const allPermIDs = new Set([...original, ...updated])

    const savePromises: Promise<any>[] = []
    allPermIDs.forEach((permID) => {
      if (original.has(permID) === updated.has(permID)) return
      const currentRoleIDs = cloneDeep(permissionRoles[permID] || [])
      const newRoleIDs = updated.has(permID)
        ? [...currentRoleIDs, editRoleID]
        : currentRoleIDs.filter((id: number) => id !== editRoleID)
      savePromises.push(updatePermissions({ permissionID: permID, roleIDs: newRoleIDs }))
    })

    if (editingAccountType) {
      savePromises.push(setAdminConfig('pagePermissions', { byAccountType: { [editingAccountType]: editPageOverrides } }))
    }

    await Promise.all(savePromises)

    const newPermissionRoles = cloneDeep(permissionRoles)
    allPermIDs.forEach((permID) => {
      if (original.has(permID) === updated.has(permID)) return
      if (updated.has(permID)) {
        newPermissionRoles[permID] = [...(newPermissionRoles[permID] || []), editRoleID]
      } else {
        newPermissionRoles[permID] = (newPermissionRoles[permID] || []).filter((id: number) => id !== editRoleID)
      }
    })
    setPermissionRoles(newPermissionRoles)

    if (editingAccountType) {
      const nextByAccountType = { ...byAccountType, [editingAccountType]: cloneDeep(editPageOverrides) }
      setByAccountType(nextByAccountType)
      dispatch(
        setGlobalConfig(GlobalConfigType.PagePermissions, {
          ...pagePermissionsGlobal,
          byAccountType: nextByAccountType,
        }),
      )
    }

    setIsLoading(false)
    setEditRoleID(-1)
    setAction('')
  }

  const onCancel = () => {
    setEditRoleID(-1)
    setEditPageOverrides({})
  }

  const countDisabledPages = (accountType: string) =>
    Object.values(byAccountType[accountType] || {}).filter((v) => v === false).length

  const renderPageTemplate = (template: IProjectPermissionTemplate, index: number, parentKey: string = '') => {
    const { key, title, data } = template
    const isTemplates = data instanceof Array
    const thisKey = `${parentKey}-${key}`
    const globallyOn = isGloballyEnabled(thisKey)
    const isActive = globallyOn && editPageOverrides[thisKey] !== false
    const EIcon = isActive ? EyeIcon : EyeSlashIcon

    return (
      <div key={`${thisKey}-${key}`} className={`${isActive ? '' : 'opacity-50'}`}>
        <span
          className={`flex items-center gap-2 text-sm ${parentKey == '' ? 'font-semibold' : ''} ${
            isActive ? '' : 'line-through'
          } ${globallyOn ? 'cursor-pointer hover:underline' : ''}`}
          title={
            globallyOn
              ? isActive
                ? 'Click to disable for this account type'
                : 'Click to inherit / enable for this account type'
              : 'Disabled in global Page Permissions'
          }
          onClick={() => globallyOn && onTogglePage(thisKey)}
        >
          {index + 1}. {title}
          {globallyOn ? (
            <EIcon className="w-4 h-4 text-shade-blue" />
          ) : (
            <EyeSlashIcon className="w-4 h-4 text-gray-300" />
          )}
        </span>

        <div className="ml-2 pl-2 border-l border-dotted">
          {data && isTemplates && data.map((v, i) => renderPageTemplate(v, i, thisKey))}
          {data &&
            !isTemplates &&
            Object.keys(data).map((childKey, childIndex) =>
              renderPageTemplate({ key: childKey, title: (data as Record<string, string>)[childKey] }, childIndex, thisKey),
            )}
        </div>
      </div>
    )
  }

  return (
    <div className="relative">
      <LayoutLoading show={isLoading} />
      <h2 className="text-2xl font-bold">
        Permissions by Account Type
        {isLoading && (
          <span className="ml-3">
            <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />
          </span>
        )}
      </h2>
      <p className="text-sm text-gray-500 mt-1 mb-2">
        Capability permissions and page visibility overrides per account type. Pages turned off in global Page
        Permissions cannot be enabled here. Use the copy icon to duplicate one account type onto another, then fine-tune
        before saving.
      </p>
      <div className="mt-4">
        {orderedRoles.map(({ key, label, roleID }, index) => {
          const permIDs = rolePermissions[roleID] || []
          const permissionLabels = permIDs
            .map((id) => {
              const name = permissions[id]
              return permissionNames[name] || name
            })
            .filter(Boolean)
            .sort()
          const disabledPageCount = countDisabledPages(key)

          return (
            <div key={key} className="mb-5">
              <div className="group flex items-center gap-2 border-b pb-1">
                <div
                  className="flex gap-2 items-center hover:text-shade-blue cursor-pointer"
                  onClick={() => onEditRole(roleID)}
                >
                  <span className="font-semibold text-sm">
                    {index + 1}. {label}
                  </span>
                  <span className="hidden group-hover:inline">
                    <PencilSquareIcon className="w-4 h-4" />
                  </span>
                </div>
                {roleID !== editRoleID && (
                  <Tooltip message="Copy these permissions to another account type">
                    <span
                      className={`text-shade-blue p-0.5 rounded ${
                        editRoleID !== -1 ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover:bg-blue-50'
                      }`}
                      onClick={() => onCopyRoleTo(roleID)}
                    >
                      <DocumentDuplicateIcon className="w-4 h-4" />
                    </span>
                  </Tooltip>
                )}
                {disabledPageCount > 0 && (
                  <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">
                    {disabledPageCount} page{disabledPageCount === 1 ? '' : 's'} disabled
                  </span>
                )}
              </div>

              <div className="mt-2 px-2">
                {roleID === editRoleID ? (
                  <div className="bg-stone-100 p-4 rounded">
                    <div className="flex flex-wrap items-end gap-4 mb-4">
                      <div className="w-72">
                        <Select2
                          id={`copy-from-${roleID}`}
                          title="Copy from another account type"
                          hasDefaultOption
                          defaultOptionText="- Select account type -"
                          options={orderedRoles
                            .filter((r) => r.roleID !== roleID)
                            .reduce((acc, r) => {
                              acc[r.key] = r.label
                              return acc
                            }, {} as Record<string, string>)}
                          value=""
                          onChange={onCopyFrom}
                        />
                      </div>
                      <p className="text-xs text-gray-500 pb-2">
                        Copies capability permissions and page visibility. Fine-tune, then Save.
                      </p>
                    </div>
                    <p className="font-semibold text-sm border-b border-gray-300 mb-2 pb-1">Capability Permissions</p>
                    {groupedPermissions.map((group, groupIndex) => (
                      <div key={group.category} className="mb-4">
                        <p className="font-semibold text-sm border-b border-gray-300 mb-2 pb-1">
                          {String.fromCharCode(65 + groupIndex)}. {group.category}
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                          {group.items.map(({ id, displayName, shortName }) => (
                            <div key={id} title={displayName} className="min-w-0">
                              <Checkbox
                                id={String(id)}
                                title={shortName}
                                fontClass="truncate"
                                value={editPermissions.includes(id)}
                                onChange={(value) => onTogglePermission(id, value)}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}

                    <p className="font-semibold text-sm border-b border-gray-300 mb-2 pb-1 mt-6">
                      Page Permissions
                      <span className="font-normal text-xs text-gray-500 ml-2">
                        (eye open = inherit/allow; eye closed = hide for this account type)
                      </span>
                    </p>
                    {pageTemplates[0] ? (
                      <div className="grid md:grid-cols-2 gap-4 mb-4 bg-white p-3 rounded border">
                        <div>{renderPageTemplate(pageTemplates[0], 0)}</div>
                        <div>
                          {pageTemplates.map((template, idx) => idx > 0 && renderPageTemplate(template, idx))}
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 mb-4 italic">No page permission template loaded.</p>
                    )}

                    <div className="flex flex-wrap gap-6 justify-center mt-4">
                      <Button link onClick={onSelectAll} className="py-[3px]">
                        <span className="text-primary">Select All Capabilities</span>
                      </Button>
                      <Button link onClick={onEnableAllPages} className="py-[3px]">
                        <span className="text-primary">Enable All Pages</span>
                      </Button>
                      <Button onClick={onSaveChanges} className="py-[3px]" loading={action === 'save'}>
                        Save Changes
                      </Button>
                      <Button link onClick={onCancel} className="py-[3px]">
                        <span className="text-red-500">Cancel</span>
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-1 text-xs text-gray-500 mt-1">
                    {permissionLabels.length === 0 ? (
                      <span className="italic text-gray-400">No permissions assigned</span>
                    ) : (
                      permissionLabels.map((permLabel, i) => (
                        <span key={i} className="border px-2 py-0.5 rounded bg-gray-50">
                          {permLabel}
                        </span>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
